import React, { useMemo } from 'react';
import Modal from '../../../../shared/components/modals/Modal';
import { formatPercentage } from '../../../../utils/numberUtils';
import './TeamWorkloadShareHelpModal.css';

const SHARE_HIGH = 1.35;
const SHARE_LOW = 0.65;

function formatRatio(ratio) {
  if (ratio == null) return '—';
  return `${Number(ratio).toFixed(1).replace(/\.0$/, '')}×`;
}

function formatUnits(units) {
  const value = Number(units) || 0;
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
}

function statusWords(status) {
  if (status === 'above_share') return 'Heavy queue';
  if (status === 'below_share') return 'Light queue';
  if (status === 'available') return 'Available';
  if (status === 'no_coverage') return 'No devices';
  return 'Fair queue';
}

function percent(value) {
  if (value == null) return '—';
  return formatPercentage(value, { decimals: 0 });
}

export default function TeamWorkloadShareHelpModal({ isOpen, onClose, technicians = [] }) {
  const math = useMemo(() => {
    const teamOpen = technicians.reduce((sum, row) => sum + (row.active || 0), 0);
    const teamUnits = technicians.reduce((sum, row) => sum + (row.coverage?.units || 0), 0);
    const examples = technicians
      .filter((row) => (row.coverage?.units || 0) > 0 && row.share?.open_ratio != null)
      .slice(0, 3);
    return {
      teamCount: technicians.length,
      teamOpen,
      teamUnits,
      examples,
    };
  }, [technicians]);

  const worked = math.examples[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="How Share is calculated" size="large">
      <div className="share-help">
        <p className="share-help__lead">
          Numbers below are from <strong>this location, right now</strong>
          {' '}({math.teamCount} technician{math.teamCount === 1 ? '' : 's'}).
          Departments are not in this formula — only assigned devices and open tickets.
        </p>

        <div className="share-help__totals">
          <div>
            <span>Open tickets on these technicians</span>
            <strong>{math.teamOpen}</strong>
          </div>
          <div>
            <span>Coverage units (primary + ½ secondary)</span>
            <strong>{formatUnits(math.teamUnits)}</strong>
          </div>
        </div>

        <section>
          <h3>Formula</h3>
          <ol className="share-help__steps">
            <li>
              Coverage units = primary devices + (secondary devices × ½)
            </li>
            <li>
              Expected % = this person’s units ÷ {formatUnits(math.teamUnits)} team units
            </li>
            <li>
              Queue % = this person’s open tickets ÷ {math.teamOpen} team open tickets
            </li>
            <li>
              Share = queue % ÷ expected %
              {' '}(<strong>1.0×</strong> means the queue matches assigned devices)
            </li>
          </ol>
          <p className="share-help__thresholds">
            Status uses that multiplier: <strong>Heavy</strong> if ≥ {SHARE_HIGH}×,
            {' '}<strong>Light</strong> if ≤ {SHARE_LOW}×, otherwise <strong>Fair</strong>.
          </p>
        </section>

        {worked && (
          <section>
            <h3>Using {worked.name} from this table</h3>
            <p>
              {worked.name} has <strong>{worked.active}</strong> open of {math.teamOpen}
              {' '}({percent(worked.share.open_percent)} of the queue) and{' '}
              <strong>{formatUnits(worked.coverage.units)}</strong> coverage units of {formatUnits(math.teamUnits)}
              {' '}({percent(worked.share.expected_percent)} expected).
              {' '}{percent(worked.share.open_percent)} ÷ {percent(worked.share.expected_percent)}{' '}
              = <strong>{formatRatio(worked.share.open_ratio)}</strong>
              {' '}→ {statusWords(worked.status)}.
            </p>
          </section>
        )}

        {math.examples.length > 0 && (
          <section>
            <h3>Same math for the first rows on screen</h3>
            <table className="share-help__table">
              <thead>
                <tr>
                  <th>Technician</th>
                  <th>Coverage</th>
                  <th>Load</th>
                  <th>Of queue</th>
                  <th>Expected</th>
                  <th>Share</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {math.examples.map((row) => (
                  <tr key={row.id || row.name}>
                    <td>{row.name}</td>
                    <td>
                      {row.coverage.primary} primary + {row.coverage.secondary} secondary = {formatUnits(row.coverage.units)}
                    </td>
                    <td>{row.active}</td>
                    <td>{percent(row.share.open_percent)}</td>
                    <td>{percent(row.share.expected_percent)}</td>
                    <td>{formatRatio(row.share.open_ratio)}</td>
                    <td>{statusWords(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="share-help__note">
              Same load can mean different share. More devices → a larger expected slice of the queue,
              so the same ticket count looks closer to fair.
            </p>
          </section>
        )}
      </div>
    </Modal>
  );
}
